import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, ChannelFullDTO, FixedAspectRatioImageDTO, SingleFileUpload } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { Observable } from 'rxjs';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { ImageUploadModel } from 'src/app/services/gui/image/model';
import { Clipping } from 'src/app/components/image/model';
import { ImageUploadService } from 'src/app/services/gui/image/image-upload.service';
import { NavigationService } from '@ngx-seatback/utils';
import { PermissionService } from '../../../../services/permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-channel-overview',
  templateUrl: './channel-overview.component.html',
  styleUrls: ['./channel-overview.component.scss'],
  standalone: false,
})
export class ChannelOverviewComponent implements OnInit {
  debounceTimer: ReturnType<typeof setTimeout> = null;

  sub: any;
  channelId: any;
  channel: ChannelFullDTO;

  avatarUrl: string = './assets/img/no-image.png';
  ambianceUrl: string = './assets/img/channel-placeholder.png';

  logoImageUpload: ImageUploadModel;
  AmbianceImageUpload: ImageUploadModel;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    private modal: ModalService,
    private imageUploadService: ImageUploadService,
    private errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    private navService: NavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.AmbianceImageUpload = new ImageUploadModel();
  }

  ngOnInit() {
    this.sub = this.activatedRoute.parent.params.subscribe(params => {
      this.channelId = +params.channelID;
      this.refreshFromServer();
    });
  }

  refreshFromServer() {
    this.api
      .ss_nm_Channel_retrieveById(this.channelId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (e: ChannelFullDTO) => {
          this.channel = e;
          this.avatarUrl = this.channel?.logo
            ? this.util.appendQueryParamToUrl(this.channel.logo.url as unknown as string, 'thumbnail=L')
            : './assets/img/no-image.png';
          this.ambianceUrl = this.channel?.ambianceImage
            ? this.util.appendQueryParamToUrl(this.channel.ambianceImage.url as unknown as string, '')
            : './assets/img/channel-placeholder.png';
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }

  pickLogo() {
    this.logoImageUpload?.destroySubscriptions();
    this.api
      .ss_nm_Channel_retrieveById(this.channelId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (channel: ChannelFullDTO) => {
          this.logoImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.logo'));
          this.logoImageUpload.imageTransfer.image = channel?.logo;
          this.logoImageUpload.subClippingChange = this.logoImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.logoClippingChange(res);
          });
          this.logoImageUpload.subFileChange = this.logoImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.logoFileChange(file);
          });
          this.logoImageUpload.subDeletePress = this.logoImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.logoDeletePress();
          });
          this.logoImageUpload.subModalClosed = this.logoImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.logoUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.logoImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.logoImageUpload.imageTransfer.image;
      this.logoImageUpload.imageTransfer.image = null;
      this.logoImageUpload.imageTransfer.uploading = true;
      this.api
        .ss_nm_Channel_clipLogo(this.channelId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.logoImageUpload.imageTransfer.image = image;
            this.logoImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  logoFileChange(file: SingleFileUpload) {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Channel_uploadLogo(this.channelId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (image: FixedAspectRatioImageDTO) => {
          this.logoImageUpload.imageTransfer.image = image;
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoDeletePress() {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Channel_deleteLogo(this.channelId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.logoImageUpload.imageTransfer.image = null;
          }
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoUploadModalClosed() {
    this.logoImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  pickAmbiance() {
    this.AmbianceImageUpload?.destroySubscriptions();
    this.api
      .ss_nm_Channel_retrieveById(this.channelId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (channel: ChannelFullDTO) => {
          this.AmbianceImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.ambiance'));
          this.AmbianceImageUpload.imageTransfer.image = channel?.ambianceImage;
          this.AmbianceImageUpload.subClippingChange = this.AmbianceImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.ambianceClippingChange(res);
          });
          this.AmbianceImageUpload.subFileChange = this.AmbianceImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.ambianceFileChange(file);
          });
          this.AmbianceImageUpload.subDeletePress = this.AmbianceImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.ambianceDeletePress();
          });
          this.AmbianceImageUpload.subModalClosed = this.AmbianceImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.ambianceUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.AmbianceImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.AmbianceImageUpload.imageTransfer.image;
      this.AmbianceImageUpload.imageTransfer.image = null;
      this.AmbianceImageUpload.imageTransfer.uploading = true;
      this.api
        .ss_nm_Channel_clipAmbianceImage(this.channelId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.AmbianceImageUpload.imageTransfer.image = image;
            this.AmbianceImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  ambianceFileChange(file: SingleFileUpload) {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Channel_uploadAmbianceImage(this.channelId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.AmbianceImageUpload.imageTransfer.image = res;
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceDeletePress() {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Channel_deleteAmbianceImage(this.channelId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.AmbianceImageUpload.imageTransfer.image = null;
          }
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceUploadModalClosed() {
    this.AmbianceImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  emitChange() {
    this.updateDebounced();
  }

  updateDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.update();
    }, 1000);
  }

  update() {
    let obs: Observable<ChannelFullDTO>;
    if (this.channel.name) {
      if (this.channel.id) {
        obs = this.api.ss_nm_Channel_updateById(this.channel);
      } else {
        obs = this.api.ss_nm_Channel_create(this.channel);
      }

      obs.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe({
        next: (res: ChannelFullDTO) => {
          this.channel = res;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    }
  }

  lockChannel() {
    if (this.channel?.id) {
      this.api
        .ss_nm_Channel_lockChannel(this.channel.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (res: ChannelFullDTO) => {
            this.channel = res;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  deleteChannel() {
    this.modal
      .confirm({
        title: this.translate.instant('lang.globals.deleteElementValue', { value: this.channel.name }),
        message: this.translate.instant('lang.globals.deleteElementValueQuestion', { value: this.channel.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ss_nm_Channel_deleteById(this.channel.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(this.util.RoutingID.channels);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }
}
